import React from "react"
import Mark from "mark.js"
import Result from "./Result"
import skippedImg from "../assets/skipped.png"
import wrongImg from "../assets/wrong.png"

// Cookie Storage Class
class GameCookieStorage {
    constructor() {
        this.cookieName = 'jungleJumbleDaily';
    }

    getTodayKey() {
        const now = new Date();
        const year = now.getUTCFullYear();
        const month = String(now.getUTCMonth() + 1).padStart(2, '0');
        const day = String(now.getUTCDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    getNextGameReset() {
        const now = new Date();
        const nextMidnight = new Date(now);
        nextMidnight.setUTCDate(now.getUTCDate() + 1);
        nextMidnight.setUTCHours(0, 0, 0, 0);
        return nextMidnight;
    }

    setCookie(name, value) {
        const expires = this.getNextGameReset();
        document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
    }

    getCookie(name) {
        const nameEQ = name + "=";
        const ca = document.cookie.split(';');
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) === ' ') c = c.substring(1, c.length);
            if (c.indexOf(nameEQ) === 0) {
                return decodeURIComponent(c.substring(nameEQ.length, c.length));
            }
        }
        return null;
    }

    saveGameState(gameState) {
        const today = this.getTodayKey();
        const dataToSave = {
            date: today,
            songId: gameState.songId,
            ...gameState,
            timestamp: Date.now()
        };
        
        try {
            const jsonString = JSON.stringify(dataToSave);
            this.setCookie(this.cookieName, jsonString);
            return true;
        } catch (error) {
            return false;
        }
    }

    loadGameState() {
        const today = this.getTodayKey();
        
        try {
            const cookieValue = this.getCookie(this.cookieName);
            if (!cookieValue) {
                return null;
            }

            const gameData = JSON.parse(cookieValue);
            
            if (gameData.date !== today) {
                this.clearGameState();
                return null;
            }

            return gameData;
        } catch (error) {
            this.clearGameState();
            return null;
        }
    }

    clearGameState() {
        document.cookie = `${this.cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/`;
    }
}

export default function Game(props){

    const gameStorage = new GameCookieStorage();

    const [songs, setSongs] = React.useState([])
    const [query, setQuery] = React.useState("")
    const [visibility, setVisibility] = React.useState("hidden")
    const [songSeconds, setSongSeconds] = React.useState(1000)
    const [numSkips, setNumSkips] = React.useState(0)
    const initialGuesses = ["", "", "", "", "", ""];
    const [isSkipped, setIsSkipped] = React.useState([
        false, false, false, false, false, false
    ])
    const [isWrong, setIsWrong] = React.useState([
        false, false, false, false, false, false
    ])
    const [skipSeconds, setSkipSeconds] = React.useState(1)
    const [btnSrc, setBtnSrc] = React.useState("play_btn.png")
    const [loadSeconds, setLoadSeconds] = React.useState("00")
    const [guessTitleBackground, setGuessTitleBackground] = React.useState({
        guess_title_0: "none",
        guess_title_1: "none",
        guess_title_2: "none",
        guess_title_3: "none",
        guess_title_4: "none",
        guess_title_5: "none",
    })
    const [isGameOver, setIsGameOver] = React.useState(false)
    const [isSuccessfulGuess, setIsSuccessfulGuess] = React.useState(false)
    const [selectedSong, setSelectedSong] = React.useState("")
    const [isSearching, setIsSearching] = React.useState(false)

    const [guesses, setGuesses] = React.useState([...initialGuesses]);
    const [gameOver, setGameOver] = React.useState(false);
    const [currentSongIndex, setCurrentSongIndex] = React.useState(0);

    const gridLayoutRef = React.useRef(null)
    const searchInputRef = React.useRef(null)
    const markInstance = React.useRef(null)
    const interval = React.useRef(null)
    const searchContainerRef = React.useRef(null)

    const controller = new AbortController()
    const song = React.useMemo(() => new Audio(props.todaysSong.preview), [props.todaysSong.preview])

    function onResetGame() {
        setGuesses([...initialGuesses]);
        setIsWrong([false, false, false, false, false, false]);
        setIsSkipped([false, false, false, false, false, false]);
        setIsSuccessfulGuess(false);
        setNumSkips(0);
        setGameOver(false);
        setIsGameOver(false);
        setCurrentSongIndex(0);
        setSongSeconds(1000);
        setSkipSeconds(1);
        setGuessTitleBackground({
            guess_title_0: "none",
            guess_title_1: "none",
            guess_title_2: "none",
            guess_title_3: "none",
            guess_title_4: "none",
            guess_title_5: "none",
        });
        setSelectedSong("");
        setQuery("");
        setVisibility("hidden");
        setIsSearching(false);

        for (let i = 0; i < 6; i++) {
            const element = document.getElementById(`guess_title_${i}`);
            if (element) {
                element.value = "";
                element.style.backgroundImage = "none";
            }
        }

        if (searchInputRef.current) {
            searchInputRef.current.value = "";
        }

        for (let i = 2; i <= 6; i++) {
            const elem = document.getElementById(`time-${i}`);
            if (elem) {
                elem.style.width = "0%";
            }
        }

        if (song) {
            song.pause();
            song.currentTime = 0;
        }
        setBtnSrc("play_btn.png");
        setLoadSeconds("00");

        if (interval.current) {
            clearInterval(interval.current);
            interval.current = null;
        }

        gameStorage.clearGameState();
    }

    React.useEffect(() => {
        const savedState = gameStorage.loadGameState();
        
        if (savedState && savedState.songId === props.todaysSong.id) {
            const reconstructedBackground = {
                guess_title_0: "none",
                guess_title_1: "none", 
                guess_title_2: "none",
                guess_title_3: "none",
                guess_title_4: "none",
                guess_title_5: "none",
            };
            
            for (let i = 0; i < 6; i++) {
                if (savedState.isSkipped && savedState.isSkipped[i]) {
                    reconstructedBackground[`guess_title_${i}`] = skippedImg;
                } else if (savedState.isWrong && savedState.isWrong[i]) {
                    reconstructedBackground[`guess_title_${i}`] = wrongImg;
                }
            }
            
            setNumSkips(savedState.numSkips);
            setIsSkipped(savedState.isSkipped);
            setIsWrong(savedState.isWrong);
            setGuessTitleBackground(reconstructedBackground);
            setIsGameOver(savedState.isGameOver);
            setIsSuccessfulGuess(savedState.isSuccessfulGuess);
            setSongSeconds(savedState.songSeconds);
            setSkipSeconds(savedState.skipSeconds);
            
            setTimeout(() => {
                savedState.guesses.forEach((guess, index) => {
                    const element = document.getElementById(`guess_title_${index}`);
                    if (element && guess) {
                        element.value = guess;
                    }
                });
                
                for (let i = 1; i <= savedState.numSkips; i++) {
                    const elem = document.getElementById(`time-${i + 1}`);
                    if (elem) {
                        elem.style.width = '100%';
                    }
                }
            }, 100);
        }
    }, [props.todaysSong.id]);

    React.useEffect(() => {
        if (gridLayoutRef.current && !markInstance.current) {
            markInstance.current = new Mark(gridLayoutRef.current);
        }
    }, []);

    React.useEffect(() => {
        if (numSkips > 0 || isGameOver) {
            setTimeout(() => {
                const guesses = [];
                for (let i = 0; i < 6; i++) {
                    const element = document.getElementById(`guess_title_${i}`);
                    guesses.push(element ? element.value : '');
                }
                
                const gameState = {
                    songId: props.todaysSong.id,
                    numSkips,
                    isSkipped,
                    isWrong,
                    guessTitleBackgroundKeys: Object.keys(guessTitleBackground).filter(key => 
                        guessTitleBackground[key] !== "none"
                    ),
                    isGameOver,
                    isSuccessfulGuess,
                    songSeconds,
                    skipSeconds,
                    guesses,
                    completedAt: isGameOver ? new Date().toISOString() : null
                };
                
                gameStorage.saveGameState(gameState);
            }, 50);
        }
    }, [numSkips, isSkipped, isWrong, guessTitleBackground, isGameOver, isSuccessfulGuess, songSeconds, skipSeconds, props.todaysSong.id]);

    React.useEffect(() => {
        function handleClickOutside(event) {
            if (searchContainerRef.current && 
                !searchContainerRef.current.contains(event.target) &&
                visibility === "visible") {
                setVisibility("hidden");
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [visibility]);

    React.useEffect(() => {
        function handleEscape(event) {
            if (event.key === "Escape" && visibility === "visible") {
                setVisibility("hidden");
            }
        }

        document.addEventListener("keydown", handleEscape);
        return () => {
            document.removeEventListener("keydown", handleEscape);
        };
    }, [visibility]);

    const options = {
        method: 'GET'
    };

    async function getSearchSongs(query, signal) {
        async function presentResults(query){
            return new Promise(async(resolve, reject) => {
                if (signal?.aborted){
                    reject(signal.reason)
                }
            
                const url = `/search?q=${encodeURIComponent(query)}`;

                try {
                    const res = await fetch(url);

                    if (!res.ok) {
                        throw new Error(`HTTP error! status: ${res.status}`);
                    }
                    
                    const data = await res.json();

                    if (data.data !== undefined){
                        resolve(data.data)
                    } else {
                        resolve([]);
                    }
                    
                } catch (error) {
                    reject(error);
                }
            })
        }

        try {
            const search_results = await presentResults(query)

            var songResults = []

            search_results.forEach(x => {
                if (!songResults.some(e => e.title === x.title && e.artist === x.artist.name)){
                    songResults.push({title: x.title, title_short: x.title_short, artist: x.artist.name})
                }
            })

            setSongs(songResults)

            const container = gridLayoutRef.current;

            if (container != null){

                while (container.firstChild) {
                    container.removeChild(container.firstChild);
                }

                if (songResults.length === 0) {
                    const div = document.createElement("div");
                    div.className = "grid-layout-error";
                    div.innerText = "No results found";
                    container.appendChild(div);
                } else {
                    for (var i=0; i<songResults.length; i++){
                        const div = document.createElement("div");
                        div.id = "song-" + i
                        div.className = "grid-layout-song";
                        var songTitleArtist = songResults[i].artist + " - " + songResults[i].title
                        div.innerText = songTitleArtist
                        container.appendChild(div);
                    }
                }

                if (container.firstChild){
                    setVisibility("visible")
                }
            }

            if (markInstance.current) {
                markInstance.current.unmark()
                markInstance.current.mark(query)
            }

        } catch(e){
            const container = gridLayoutRef.current;
            if (container != null){
                while (container.firstChild) {
                    container.removeChild(container.firstChild);
                }
                const div = document.createElement("div");
                div.className = "grid-layout-searching";
                div.innerText = "Searching..."
                container.appendChild(div);
                setVisibility("visible");
            }
        } finally {
            setIsSearching(false);
        }
    }

    const debounce = (fn, delay = 500) => { 
        let timerId = null; 
        return (...args) => { 
            clearTimeout(timerId); 
            timerId = setTimeout(() => fn(...args), delay); 
        }; 
    };

    const debouncedApiCall = debounce(getSearchSongs);

    function showSearchingIndicator() {
        const container = gridLayoutRef.current;
        if (container != null) {
            while (container.firstChild) {
                container.removeChild(container.firstChild);
            }
            
            const div = document.createElement("div");
            div.className = "grid-layout-searching";
            div.innerText = "Searching...";
            container.appendChild(div);
            setVisibility("visible");
        }
    }

    React.useEffect(() => {
        const signal = controller.signal

        if (query.length === 0){
            setSongs([])
            setVisibility("hidden")
            setIsSearching(false)
            return
        }

        setIsSearching(true);
        showSearchingIndicator();

        debouncedApiCall(query, signal);

        return () => {
            controller.abort("cancel")
        }

    }, [query])

    React.useEffect(() => {

        const handleSongClick = (e) => {
            e.preventDefault();
            e.stopPropagation();

            setVisibility("hidden");

            const song_id = e.currentTarget.id.split("-")[1];
            const songData = songs[song_id];

            if (songData) {
                const fullTitle = `${songData.artist} - ${songData.title}`;

                setSelectedSong(fullTitle);

                if (searchInputRef.current) {
                    searchInputRef.current.value = fullTitle;
                    searchInputRef.current.dataset.artist = songData.artist;
                    searchInputRef.current.dataset.title = songData.title;
                }
            }
        };

        const handleInput = (e) => {
            e.preventDefault()
            e.stopPropagation();

            if ((songs.length > 0 || isSearching) && query.length > 0){
                setVisibility("visible")
            }
            else {
                setVisibility("hidden")
            }
        }

        var songElements = document.getElementsByClassName("grid-layout-song");
        
        if (searchInputRef.current) {
            searchInputRef.current.addEventListener("click", handleInput);
        }

        for (var i=0; i<songElements.length; i++) {
            songElements[i].addEventListener("click", handleSongClick);
        }

        return () => {
            for (var i=0; i<songElements.length; i++) {
                if (songElements[i]) {
                    songElements[i].removeEventListener("click", handleSongClick);
                }
            }
            if (searchInputRef.current) {
                searchInputRef.current.removeEventListener('click', handleInput);
            }
        }
    }, [query, songs, selectedSong, isSearching])

    function inputChange(value){
        if (value != null){
            setQuery(value)
            setSelectedSong("")
        }
        else {
            setSelectedSong(selectedSong)
        }
    }

    function previewSong(){

        setLoadSeconds("00")

        if (!song.paused){
            song.pause()
            song.currentTime = 0
            setBtnSrc("play_btn.png")
            setLoadSeconds("00")
            clearInterval(interval.current)
            interval.current = null
            return
        }

        song.pause()
        song.currentTime = 0

        function loadTimeBar() {
            interval.current = setInterval(time, 1000)

            function time(){

                setLoadSeconds((prevLoadSeconds) => {
                    var secs = parseInt(prevLoadSeconds)
                    secs += 1
                    if (secs < 10){
                        secs = "0" + secs
                    }
                    if (secs >= songSeconds/1000){
                        clearInterval(interval.current)
                        interval.current = null
                        setBtnSrc("play_btn.png")
                        song.pause()
                    }
                    return secs
                })
            }
        }
        
        var playPromise = song.play()

        if (playPromise !== undefined){
            playPromise.then(function() {
                setBtnSrc("pause_btn.png")
                loadTimeBar()
            })
            .catch(error => {
                // Audio play failed
            })
        }
    }

    function skipSongSeconds(skipped){

        var guess_title = document.getElementById("guess_title_" + numSkips)

        var skip_icon = skippedImg

        if (skipped){ 
            guess_title.value = "SKIPPED"
            setIsSkipped( prevIsSkipped => {
                const result = [...prevIsSkipped];
                result[numSkips] = true;
                return result;
            })
        }
        else {
            var song_selected = searchInputRef.current ? searchInputRef.current.value : "";
            guess_title.value = song_selected
            skip_icon = wrongImg
        }

        setGuessTitleBackground( prevGuessTitleBackground => ({
            ...prevGuessTitleBackground,
            ["guess_title_" + numSkips]: skip_icon
        }))

        if (numSkips === 5){
            setIsGameOver(true)
            return
        }

        song.pause()
        song.currentTime = 0
        setBtnSrc("play_btn.png")
        setLoadSeconds("00")
        clearInterval(interval.current)
        interval.current = null

        var elem2 = document.getElementById("time-2");
        var elem3 = document.getElementById("time-3");  
        var elem4 = document.getElementById("time-4");
        var elem5 = document.getElementById("time-5");  
        var elem6 = document.getElementById("time-6");

        setNumSkips((prevNumSkips) => {
            var newNumSkips = prevNumSkips + 1

            if (newNumSkips === 0){
                setSongSeconds(1000)
                setSkipSeconds(1)
            }
            else if (newNumSkips === 1){
                setSongSeconds(2000)
                setSkipSeconds(2)
                elem2.style.width = '100%';
            }
            else if (newNumSkips === 2){
                setSongSeconds(4000)
                setSkipSeconds(3)
                elem3.style.width = '100%';
            }
            else if (newNumSkips === 3){
                setSongSeconds(7000)
                setSkipSeconds(4)
                elem4.style.width = '100%';
            }
            else if (newNumSkips === 4){
                setSongSeconds(11000)
                setSkipSeconds(5)
                elem5.style.width = '100%';
            }
            else if (newNumSkips === 5){
                setSongSeconds(16000)
                elem6.style.width = '100%';
            }

            return newNumSkips
        })     
    }

    function normalizeString(str) {
        return str
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/\s+/g, " ")
            .trim();
    }

    function submitAnswer() {
        const song = searchInputRef.current ? searchInputRef.current.value : "";

        if (song.trim().length === 0) {
            skipSongSeconds(true);
            clearSearchBox();
            return;
        }

        let song_artist = searchInputRef.current?.dataset.artist || "";
        let song_title = searchInputRef.current?.dataset.title || "";

        if (!song_artist || !song_title) {
            const parts = song.split(" - ");
            if (parts.length >= 2) {
                song_artist = parts[0];
                song_title = parts.slice(1).join(" - ");
            }
        }

        // Get today's song info - check both full title and title_short
        const todays_song_title_full = props.todaysSong.title;
        const todays_song_title_short = props.todaysSong.title_short;
        const todays_song_artist = props.todaysSong.artist.name;

        const normalizedArtist = normalizeString(song_artist);
        const normalizedTitle = normalizeString(song_title);
        const normalizedTodaysArtist = normalizeString(todays_song_artist);
        const normalizedTodaysTitleFull = normalizeString(todays_song_title_full);
        const normalizedTodaysTitleShort = normalizeString(todays_song_title_short);

        // Check if artist matches AND title matches either full or short version
        const artistMatches = normalizedArtist === normalizedTodaysArtist;
        const titleMatches = normalizedTitle === normalizedTodaysTitleFull || 
                            normalizedTitle === normalizedTodaysTitleShort;

        if (artistMatches && titleMatches) {
            setIsGameOver(true);
            setIsSuccessfulGuess(true);
        } else {
            skipSongSeconds();
            setIsWrong(prevIsWrong => {
                const result = [...prevIsWrong];
                result[numSkips] = true;
                return result;
            });
            clearSearchBox();
        }
    }

    function clearSearchBox() {
        if (searchInputRef.current) {
            searchInputRef.current.value = "";
        }
        setQuery("");
        setSelectedSong("");
        setVisibility("hidden");
    }

    if (!isGameOver){
        return (
            <div>
                <div className="game-guess-title">
                    <input id="guess_title_0" type="text" disabled style={{backgroundImage: guessTitleBackground.guess_title_0 !== "none" ? "url("+guessTitleBackground.guess_title_0+")" : "none"}} />
                    <input id="guess_title_1" type="text" disabled style={{backgroundImage: guessTitleBackground.guess_title_1 !== "none" ? "url("+guessTitleBackground.guess_title_1+")" : "none"}} />
                    <input id="guess_title_2" type="text" disabled style={{backgroundImage: guessTitleBackground.guess_title_2 !== "none" ? "url("+guessTitleBackground.guess_title_2+")" : "none"}} />
                    <input id="guess_title_3" type="text" disabled style={{backgroundImage: guessTitleBackground.guess_title_3 !== "none" ? "url("+guessTitleBackground.guess_title_3+")" : "none"}} />
                    <input id="guess_title_4" type="text" disabled style={{backgroundImage: guessTitleBackground.guess_title_4 !== "none" ? "url("+guessTitleBackground.guess_title_4+")" : "none"}} />
                    <input id="guess_title_5" type="text" disabled style={{backgroundImage: guessTitleBackground.guess_title_5 !== "none" ? "url("+guessTitleBackground.guess_title_5+")" : "none"}} />
                </div>
                <div className="game-main">
                    <div className="outer-container">
                        <div className="inner-container">
                            <div className="game-time">
                                <span className="game-time-grid" >
                                    <div id="time-1" style={{width:"100%"}} className="game-time-grid-loaded"></div>
                                </span>
                                <span className="game-time-grid" >
                                    <div id="time-2" className="game-time-grid-loaded"></div>
                                </span>
                                <span className="game-time-grid" >
                                    <div id="time-3" className="game-time-grid-loaded"></div>
                                </span>
                                <span className="game-time-grid" >
                                    <div id="time-4" className="game-time-grid-loaded"></div>
                                </span>
                                <span className="game-time-grid" >
                                    <div id="time-5" className="game-time-grid-loaded"></div>
                                </span>
                                <span className="game-time-grid" >
                                    <div id="time-6" className="game-time-grid-loaded"></div>
                                </span>
                            </div>
                            <div className="game-time-play">
                                <p>0:{loadSeconds}</p>
                                <img 
                                    className="game-play-btn" 
                                    src={require("../assets/"+btnSrc)} 
                                    alt="play button" 
                                    width="40" 
                                    height="40" 
                                    onClick={previewSong}
                                />
                                <p>0:16</p>
                            </div>
                            <div className="game-guess-title" ref={searchContainerRef}>
                                <div 
                                    ref={gridLayoutRef}
                                    id="grid-layout" 
                                    className="grid-layout" 
                                    style={{visibility:visibility}}>
                                </div>
                                <input 
                                    ref={searchInputRef}
                                    id="search-input" 
                                    type="text" 
                                    placeholder="Know it? Search for the artist / title"
                                    onChange={event => inputChange(event.target.value)}
                                    value={selectedSong !== "" ? selectedSong : query}
                                />
                            </div>
                            <div className="game-buttons">
                                <button className="skip-btn" onClick={() => skipSongSeconds(true)}>
                                    {numSkips === 5 ? "SKIP" : `SKIP (+${skipSeconds})`}
                                    </button>
                                <button className="submit-btn" onClick={submitAnswer}>SUBMIT</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }
    else {
      return <Result
        todaysSong={props.todaysSong}
        songSeconds={songSeconds}
        isSuccessfulGuess={isSuccessfulGuess}
        numSkips={numSkips}
        isSkipped={isSkipped}
        isWrong={isWrong}
        onResetGame={onResetGame}
      />
    }  
}